import { CommonPage, Content, LoadableContent } from "common/Grid";
import { MainForm } from "../Common/MainForm";
import { toast } from "react-toastify";
import { useState } from "react";
import { useUpdateSiteConfigMutation } from "store/action/site-config";
import { useGetFileUploadMutation } from "store/action/inquiryApi";
import { captureComponentError } from "utils/sentry";

// Sensitive fields (secrets/tokens) skip client-side required validation and are
// validated server-side instead; map the backend's rejected keys back onto the form
// the same way a required-field error would show.
const buildInvalidKeyErrors = (invalidKeys, message) =>
  Object.fromEntries(invalidKeys.map((key) => [key, message || "Invalid value"]));

// The backend returns invalidKeys either at the top level or nested under
// invalidSites[0] (bulk-style response shape), depending on the endpoint.
const extractInvalidKeys = (payload) =>
  payload?.invalidKeys || payload?.invalidSites?.[0]?.invalidKeys;

const Page = () => {
  const [updateSiteConfig, { isLoading }] = useUpdateSiteConfigMutation();
  const [uploadFile] = useGetFileUploadMutation();
  const [pageLoading, setPageLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState({});

  const [userInput, setUserInput] = useState({
    squareConfig: {},
    triniteq: {},
    webhook: {},
    appConfig: {},
    mailSettings: {},
    tevalis: {},
    twilio: {},
    oneSignal: {},
    payStack: {},
    stripeConfig: {},
    clientName: "",
    filePwd: "",
    siteName: "",
    reportUrl: "",
    paymentProvider: "stripe",
    locationId: "",
    squareSync: false,
    triniteqPos: false,
    tevalisPos: false,
    siteid: "",
  });

  const handleSubmit = async () => {
    try {
      setPageLoading(true);
      setApiErrors({});

      const getFileType = (dataUrl) => {
        if (!dataUrl || typeof dataUrl !== "string") return "png";
        const mime = dataUrl.split(";")[0].split(":")[1] || "";
        if (mime.includes("icon") || mime.includes("x-icon") || mime.includes("vnd.microsoft.icon")) return "ico";
        if (mime.includes("jpeg")) return "jpg";
        return mime.split("/")[1] || "png";
      };

      let appIconUrl = userInput?.appIcon?.url || userInput?.appIcon;

      if (typeof appIconUrl === "string" && appIconUrl.startsWith("data:")) {
        const logoUploadResponse = await uploadFile({
          base64: appIconUrl,
          type: getFileType(appIconUrl)
        }).unwrap();
        if (logoUploadResponse?.status === "SUCCESS") {
          appIconUrl = logoUploadResponse.data?.url || logoUploadResponse.data;
        } else {
          toast.error("App icon upload failed");
          setPageLoading(false);
          return;
        }
      }

      let siteBannerUrl = userInput?.appConfig?.siteBanner;

      if (typeof siteBannerUrl === "string" && siteBannerUrl.startsWith("data:")) {
        const bannerUploadResponse = await uploadFile({
          base64: siteBannerUrl,
          type: "png"
        }).unwrap();
        if (bannerUploadResponse?.status === "SUCCESS") {
          siteBannerUrl = bannerUploadResponse.data?.url || bannerUploadResponse.data;
        } else {
          toast.error("Site banner upload failed");
          setPageLoading(false);
          return;
        }
      }

      const payload = await updateSiteConfig({
        body: {
          ...userInput,
          appIcon: appIconUrl,
          appConfig: {
            ...userInput.appConfig,
            siteBanner: siteBannerUrl,
          },
        },
        siteId: userInput.siteid,
      });

      const response = payload?.data;
      if (response?.status === "SUCCESS") {
        toast.success(response?.message);
      } else {
        const invalidKeys = extractInvalidKeys(response);
        if (Array.isArray(invalidKeys) && invalidKeys.length > 0) {
          setApiErrors(buildInvalidKeyErrors(invalidKeys, response?.message));
        }
        toast.error(response?.message || "Creation failed");
      }
      setPageLoading(false);
    } catch (e) {
      const invalidKeys = extractInvalidKeys(e?.data);
      if (Array.isArray(invalidKeys) && invalidKeys.length > 0) {
        setApiErrors(buildInvalidKeyErrors(invalidKeys, e?.data?.message));
      }
      captureComponentError(e, { component: "AddNewSite", action: "handleSubmit" });
      toast.error(e?.data?.message || "Something went wrong");
      setPageLoading(false);
    }
  };

  const handleCancel = () => {
    setUserInput(prev => ({ ...prev }));
    toast.info("Cancelled");
  };

  return (
    <CommonPage>
      <Content>
        <LoadableContent isLoading={isLoading || pageLoading}>
          <MainForm
            userInput={userInput}
            setUserInput={setUserInput}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            apiErrors={apiErrors}
          />
        </LoadableContent>
      </Content>
    </CommonPage>
  );
};

export default Page;
